import { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
	Search,
	RefreshCw,
	Pencil,
	Link2,
	X
} from 'lucide-react';
import { Pagination, Tooltip } from '@mui/material';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import useDebounce from '@Hooks/useDebounce';
import ErrorHandler from '@Root/ErrorHandler';
import LinkContacts from '@RegistriesForms/LinkContacts';
import UserUnsubscribe from '@CommonFunctionalDisplay/UserUnsubscribe';
import ContactDetailsPage from '@CommonFunctionalDisplay/ContactDetailsPage';
import RegistryLinkService from '@Helpers/URLHelpers/WeboutService/RegistryLinkService';
import RegistryLinkViewService from '@Helpers/URLHelpers/WeboutService/Views/RegistryLinkViewService';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import './ContactsNameCardPage.scss';

export default function ContactsNameCardPage(props) {
	const [cursor, setCursor] = useState('auto');
	const [edit, setEdit] = useState(false);
	const [showLinkCustomers, setShowLinkCustomers] = useState(false);
	const [nameCardList, setNameCardList] = useState([]);
	const [selectedNameCard, setSelectedNameCard] = useState(null);
	const [openNameCardView, setOpenNameCardView] = useState(false);
	const [selectedCustomers, setSelectedCustomers] = useState([]);
	const [selectedLabelId, setSelectedLabelId] = useState(0);
	const [searchQuery, setSearchQuery] = useState('');
	const [page, setPage] = useState(1);
	const [size] = useState(10);
	const [totalPages, setTotalPages] = useState(1);
	const [totalResult, setTotalResult] = useState(0);
	const { currentProjectId } = useReduxProject();
	const debouncedSearch = useDebounce(searchQuery, 500, true);
	const nameCardListRef = useRef(null);
	const errorHandler = new ErrorHandler();
	const nameCardService = new NameCardService();
	const registryLinkViewService = new RegistryLinkViewService();
	const registryLinkService = new RegistryLinkService();

	const className = 'contacts-table';

	const buildListFilter = useCallback(
		(pageNumber = 0) => ({
			nameCardFilter: {
				searchQuery: debouncedSearch,
				projectIdList: [currentProjectId],
				activeList: [true],
				sortBy: 'createTimeStamp',
				sortAsc: false,
			},
			page: debouncedSearch === '' ? pageNumber : 0,
			size,
		}),
		[debouncedSearch, currentProjectId, size],
	);

	useEffect(() => {
		renderData();
	}, [props?.selectedLabel, props?.refreshKey, page, debouncedSearch]);

	const renderData = async () => {
		setCursor('wait');

		try {
			if (props?.selectedLabel?.id === 0) {
				setSelectedLabelId(0);

				await fetchNameCards(page - 1);
			} else {
				if (debouncedSearch === '') {
					setSelectedLabelId(props?.selectedLabel?.id);

					await fetchRegistryLinksList(props?.selectedLabel);
				} else {
					await fetchNameCards(page - 1);
				}
			}
		} catch (err) {
			errorHandler.handleError(err);
		}

		setCursor('auto');
	};

	const fetchNameCards = async (apiPage = page - 1) => {
		try {
			const body = buildListFilter(apiPage);

			let response = await nameCardService.getListFiltered(body);

			response = JSON.parse(response.body);

			const list =
				response?.result?.nameCardList?.map((item) => ({
					...item,
					nameCardId: item.id,
				})) || [];

			setNameCardList(list);

			setTotalPages(response?.result?.totalPages || 1);

			setTotalResult(response?.result?.totalItems || 0);

			props?.setTotalCount(response?.result?.totalItems || 0);
		} catch (err) {
			errorHandler.handleError(err);
		}
	};

	const fetchRegistryLinksList = async (selectedLabel) => {
		try {
			const pageNumber = selectedLabel?.id === selectedLabelId ? page - 1 : 0;

			const body = {
				page: pageNumber,
				size,
				targetType: 'labelCard',
				targetId: selectedLabel?.id,
			};

			let response = await registryLinkViewService.getPageableList(body);

			if (response.status < 400) {
				response = JSON.parse(response.body);

				const list = response.leadsRegistryLinkList.map((item) => ({
					...item,
					nameCardId: item?.registryId,
				}));

				setNameCardList(list);

				props?.setTotalCount(response.totalItems);

				setTotalPages(response?.totalPages || 1);

				setTotalResult(response?.totalItems || 0);
			}
		} catch (err) {
			errorHandler.handleError(err);
		}
	};

	const reRenderData = async () => {
		try {
			const body = {
				page: 0,
				size: 10,
				targetType: 'labelCard',
				targetId: props?.selectedLabel?.id,
			};

			let response = await registryLinkViewService.getPageableList(body);

			if (response.status < 400) {
				response = JSON.parse(response.body);

				const list = response.leadsRegistryLinkList.map((item) => ({
					...item,
					nameCardId: item?.registryId,
				}));

				setNameCardList(list);

				props?.setTotalCount(response.totalItems);

				setTotalPages(response?.totalPages || 1);

				setTotalResult(response?.totalItems || 0);
			}
		} catch (err) {
			errorHandler.handleError(err);
		}
	};

	const removeLinks = async () => {
		setCursor('wait');

		try {
			const bodyObj = {
				idList: [...selectedCustomers],
				projectId: currentProjectId,
			};

			const response =
				await registryLinkService.bulkRemoveRegistryLink(bodyObj);

			if (response.status < 400) {
				await reRenderData();

				setSelectedCustomers([]);
				setEdit(false);
			}
		} catch (err) {
			errorHandler.handleError(err);
		}

		setCursor('auto');
	};

	const onSelect = (e) => {
		const currentPageIds = nameCardList.map((item) => item.id);

		const selectedIds = e.value.map((item) => item.id);

		setSelectedCustomers((prev) => {
			const remaining = prev.filter((id) => !currentPageIds.includes(id));

			return [...remaining, ...selectedIds];
		});
	};

	const avatarStyle = (name = '') => {
		const colors = [
			['#DBEAFE', '#1D4ED8'],
			['#DCFCE7', '#15803D'],
			['#FEF9C3', '#A16207'],
			['#EDE9FE', '#6D28D9'],
			['#FCE7F3', '#BE185D'],
			['#FFEDD5', '#C2410C'],
		];

		const index = (name.charCodeAt(0) || 0) % colors.length;

		return {
			background: colors[index][0],
			color: colors[index][1],
		};
	};

	const getItemName = (item) => {
		const avatar = avatarStyle(item?.name);

		return (
			<button
				type='button'
				className={`${className}__contact`}
				onClick={() => {
					setSelectedNameCard(item);
					setOpenNameCardView(true);
				}}
			>
				<span className={`${className}__avatar`} style={avatar}>
					{(item?.name || '?').charAt(0).toUpperCase()}
				</span>

				<span className={`${className}__contact-details`}>
					<strong>{item?.name || 'Unnamed contact'}</strong>

					<small>
						{item?.companyName || item?.email || 'No additional information'}
					</small>
				</span>
			</button>
		);
	};

	const getPhoneNumber = (item) => {
		return (
			<span className={`${className}__value`}>
				{item?.contactNumber || '—'}
			</span>
		);
	};

	const getEmail = (item) => {
		return <span className={`${className}__value`}>{item?.email || '—'}</span>;
	};

	const getSubscribed = (item) => {
		const nameCardObj = {
			id: item?.nameCardId,
			isSubscribed: !!item?.subscribed ? item?.subscribed : item?.isSubscribed,
		};

		return <UserUnsubscribe nameCard={nameCardObj} />;
	};

	const emptyList = () => (
		<div className={`${className}__empty`}>
			<div className={`${className}__empty-icon`}>
				<Search size={18} />
			</div>

			<strong>No contacts found</strong>

			<span>Try changing your search or selected list.</span>
		</div>
	);

	const getFooter = () => {
		if (edit) {
			return (
				<div className={`${className}__footer`}>
					<div className={`${className}__pagination`}>
						<Pagination
							count={totalPages}
							page={page}
							onChange={(_, value) => setPage(value)}
							shape='rounded'
							size='small'
						/>

						<span className={`${className}__total`}>
							{totalResult} contacts
						</span>
					</div>

					<div className={`${className}__footer-actions`}>
						<button
							type='button'
							className={`${className}__footer-btn ${className}__footer-btn--cancel`}
							onClick={() => {
								setEdit(false);
								setSelectedCustomers([]);
							}}
						>
							Cancel
						</button>

						<button
							type='button'
							className={`${className}__footer-btn ${className}__footer-btn--danger`}
							disabled={selectedCustomers.length === 0 || cursor === 'wait'}
							onClick={removeLinks}
						>
							Remove
							{selectedCustomers.length > 0 && ` (${selectedCustomers.length})`}
						</button>
					</div>
				</div>
			);
		}

		return (
			<div className={`${className}__footer`}>
				<div className={`${className}__pagination`}>
					{totalPages > 1 && (
						<Pagination
							count={totalPages}
							page={page}
							onChange={(_, value) => setPage(value)}
							shape='rounded'
							size='small'
						/>
					)}
				</div>

				<span className={`${className}__total`}>{totalResult} contacts</span>
			</div>
		);
	};

	return (
		<div className={className} style={{ cursor }}>
			<div className={`${className}__toolbar`}>
				<div className={`${className}__toolbar-left`}>
					<div className={`${className}__search`}>
						<Search size={14} />

						<input
							type='text'
							value={searchQuery}
							onChange={(e) =>
								setSearchQuery(DOMPurify.sanitize(e.target.value))
							}
							placeholder='Search contacts...'
						/>
					</div>

					<Tooltip title='Refresh' arrow>
						<button
							type='button'
							className={`${className}__icon-btn`}
							onClick={() => {
								setSearchQuery('');
								setPage(1);
								renderData();
							}}
						>
							<RefreshCw size={14} />
						</button>
					</Tooltip>

					{props?.selectedLabel?.id !== 0 && edit ? (
						<>
							<Tooltip title='Select contacts' arrow>
								<button
									type='button'
									className={`${className}__icon-btn`}
									onClick={() => {
										setSelectedCustomers([]);
										setEdit(false);
									}}
								>
									<X size={14} />
								</button>
							</Tooltip>
						</>
					) : (
                        <>
                            {selectedLabelId !== 0 && (
                                <>
                                    <Tooltip title="Select contacts" arrow>
                                        <button
                                            type="button"
                                            className={`${className}__icon-btn`}
                                            onClick={() => {
                                                setSelectedCustomers([]);
                                                setEdit(true);
                                            }}
                                        >
                                            <Pencil size={14} />
                                        </button>
                                    </Tooltip>

                                    <button
                                        type="button"
                                        className={`${className}__link-btn`}
                                        onClick={() => setShowLinkCustomers(true)}
                                    >
                                        <Link2 size={13} />
                                        Link Contact
                                    </button>
                                </>
                            )}
                        </>
					)}
				</div>

				<div className={`${className}__toolbar-right`}>
					<span className={`${className}__result-label`}>
						{totalResult} contacts
					</span>
				</div>
			</div>

			{/* TABLE */}

			<div className={`${className}__table-wrapper`}>
				<DataTable
					value={nameCardList}
					ref={nameCardListRef}
					scrollable
					scrollHeight='flex'
					style={{
						height: '100%',
					}}
					columnResizeMode='expand'
					emptyMessage={emptyList}
					footer={getFooter}
					selection={nameCardList.filter((item) =>
						selectedCustomers.includes(item.id),
					)}
					onSelectionChange={onSelect}
					resizableColumns
					dataKey='nameCardId'
				>
					{edit && (
						<Column
							selectionMode='multiple'
							style={{
								width: '42px',
							}}
						/>
					)}

					<Column field='name' header='CONTACT' body={getItemName} />

					<Column field='contactNumber' header='PHONE' body={getPhoneNumber} />

					<Column field='email' header='EMAIL' body={getEmail} />

					<Column
						field='subscribed'
						header='SUBSCRIPTION'
						body={getSubscribed}
					/>
				</DataTable>
			</div>
			{openNameCardView && (
				<ContactDetailsPage
					reloadData={() => renderData()}
					nameCardId={selectedNameCard?.nameCardId}
					onClose={() => {
						setSelectedNameCard(null);
						setOpenNameCardView(false);
					}}
				/>
			)}
			{showLinkCustomers && (
				<LinkContacts
					reloadData={() => reRenderData()}
					itemId={props?.selectedLabel?.id}
					onClose={() => setShowLinkCustomers(false)}
				/>
			)}
		</div>
	);
}
